import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FilterRule } from '../../../api/api-clientInformation';
import { DisplayStorageService } from '../../../services/display-storage.service';
import { Column } from '../filter-dialog/constants';
import { getFilterLabels } from './filter-labels.util';

export interface FilterBannerState {
  count: number;
  text: string;
}

const BANNER_DURATION_MS = 7000;

@Injectable({ providedIn: 'root' })
export class FilterBannerService {
  private readonly translate = inject(TranslateService);
  private readonly displayStorage = inject(DisplayStorageService);
  private readonly _banner = signal<FilterBannerState | null>(null);
  private timer?: ReturnType<typeof setTimeout>;

  readonly banner = this._banner.asReadonly();

  show(labels: string[]): void {
    if (!labels.length) return;

    const key = labels.length === 1
      ? 'COMMON.FILTER_BANNER.APPLIED_ONE'
      : 'COMMON.FILTER_BANNER.APPLIED_MANY';
    const text = this.translate.instant(key, { count: labels.length, filters: labels.join(', ') });

    this._banner.set({ count: labels.length, text });
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.dismiss(), BANNER_DURATION_MS);
  }

  /** Shows the banner for the filters saved under `storageKey` (same keys the filter dialog writes to). */
  announce(storageKey: string, columns: Column[]): void {
    let rules: FilterRule[];
    try {
      rules = JSON.parse(this.displayStorage.getFilters(storageKey) || '[]');
    } catch {
      return;
    }
    if (!Array.isArray(rules)) return;
    // translate.instant() returns raw keys until the language file has loaded (direct page load).
    this.translate.get('COMMON.FILTER_BANNER.APPLIED_MANY').subscribe(() =>
      this.show(getFilterLabels(rules, columns, this.translate)));
  }

  dismiss(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    this._banner.set(null);
  }
}
